import React, { useState, useCallback } from 'react';
import { Store, Users, Zap, Shield, Clock } from 'lucide-react';
import URLInput from '../../components/features/URLInput';
import DetectionResults from '../../components/features/DetectionResults';
import { tabbyDetectionService } from '../../services/api';
import { searchHistoryService } from '../../services/searchHistory';

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  const [error, setError] = useState(null);

  const handleURLSubmit = useCallback(async (url) => {
    console.log(`🎬 [HomePage] Starting detection for: ${url}`);
    setIsLoading(true);
    setError(null);
    setDetectionResult(null);

    try {
      const result = await tabbyDetectionService.checkTabbySupport(url);
      console.log(`🎉 [HomePage] Detection completed successfully`, result);
      setDetectionResult(result);
      
      // Add to search history
      searchHistoryService.addToHistory(result);
    } catch (err) {
      const detectionError = err;
      console.error('💥 [HomePage] Detection failed:', err);
      setError(detectionError.message || 'Failed to check website');
    } finally {
      setIsLoading(false);
      console.log(`🏁 [HomePage] Detection process ended`);
    }
  }, []);

  const handleNewSearch = useCallback(() => {
    setDetectionResult(null);
    setError(null);
    setIsLoading(false);
  }, []);

  const useCases = [
    {
      icon: <Store size={18} style={{ color: '#6B7785' }} />,
      title: 'Competitive Merchants',
      description: 'Research competitors\' payment options and identify market opportunities',
      audience: 'E-commerce managers'
    },
    {
      icon: <Users size={18} style={{ color: '#6B7785' }} />,
      title: 'BNPL-Savvy Consumers', 
      description: 'Find retailers that accept your preferred flexible payment method',
      audience: 'Smart shoppers'
    }
  ];

  const keyFeatures = [
    {
      icon: <Zap size={16} style={{ color: '#3ECC71' }} />,
      title: 'Instant Results',
      description: 'Get detection results in under 3 seconds'
    },
    {
      icon: <Shield size={16} style={{ color: '#3ECC71' }} />,
      title: 'High Accuracy',
      description: '90%+ detection accuracy with visual proof'
    },
    {
      icon: <Clock size={16} style={{ color: '#3ECC71' }} />,
      title: 'Real-Time Scanning',
      description: 'Live analysis of payment integration'
    }
  ];

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 24px' }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: '35px',
            lineHeight: '1.03',
            fontWeight: 600,
            color: '#000000', // Pure black for heading
            letterSpacing: '-0.02em',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            margin: '0 0 24px 0'
          }}>
            Tabby Website Checker
          </h1>
          
          <p style={{
            fontSize: '16px',
            color: '#6B7785', // Tabby secondary text color
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            maxWidth: '600px',
            margin: '0 auto 32px auto',
            lineHeight: 1.5
          }}>
            Instantly discover whether any online merchant accepts Tabby payments. 
            Perfect for competitive research and smart shopping decisions.
          </p>

          {/* Key Features */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            flexWrap: 'wrap', 
            gap: '16px', 
            marginBottom: '48px' 
          }}>
            {keyFeatures.map((feature, index) => (
              <div 
                key={index}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  backgroundColor: '#F8F9FA', // Light gray background
                  padding: '6px 12px',
                  borderRadius: '16px'
                }}
              >
                {feature.icon}
                <span style={{ 
                  color: '#1A202C', 
                  fontWeight: 500, 
                  fontSize: '12px' 
                }}>
                  {feature.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* URL Input Section */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '48px' 
        }}>
          <URLInput
            onURLSubmit={handleURLSubmit}
            isLoading={isLoading}
            error={error}
          />
        </div>

        {/* Detection Results */}
        {(detectionResult || isLoading) && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '48px' 
          }}>
            <DetectionResults
              result={detectionResult}
              onNewSearch={handleNewSearch}
              loading={isLoading}
            />
          </div>
        )}

        {/* Use Cases Section */}
        <div style={{ marginTop: '64px' }}>
          <h2 style={{
            fontSize: '22px',
            lineHeight: '1.09',
            fontWeight: 600,
            color: '#000000', // Black heading
            letterSpacing: '-0.01em',
            textAlign: 'center',
            margin: '0 0 32px 0'
          }}>
            Who Uses Tabby Website Checker?
          </h2>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth >= 768 ? '1fr 1fr' : '1fr', 
            gap: '24px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                style={{ 
                  backgroundColor: '#FFFFFF',
                  padding: '24px',
                  // No border or shadow - true to Tabby flat design
                  height: '100%'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ 
                    backgroundColor: '#F8F9FA', // Light gray background
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {useCase.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1A202C',
                      margin: '0 0 8px 0'
                    }}>
                      {useCase.title}
                    </h3>
                    <span style={{
                      backgroundColor: '#D1FAE5', // Success background
                      color: '#059669', // Success text  
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: 500
                    }}>
                      {useCase.audience}
                    </span>
                  </div>
                </div>
                <p style={{ 
                  color: '#6B7785',
                  lineHeight: 1.5,
                  fontSize: '14px',
                  margin: 0
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;